import React from "react"
import { useNavigate } from "react-router-dom"
import { Close, Html as HtmlIcon } from "@mui/icons-material"
import {
  Drawer,
  IconButton,
  styled,
  Chip,
  CircularProgress,
  Tooltip as MuiTooltip,
} from "@mui/material"
import {
  Button,
  TooltipProvider,
  Typography,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@kwaleeltd/react-ui"
import { ConfirmModal } from "@/components/ConfirmModal.js"
import {
  AiDataTypes,
  CreativeAiMetadataTypes,
  CreativeObjectType,
} from "@/model/creative"
import {
  useDeleteCreativeMutation,
  useGetAllAssetCategoriesQuery,
} from "@/app/services/tags"
import { useAppDispatch, useAppSelector } from "@/app/hooks"
import { useGetAllNetworksQuery } from "@/app/services/addNetworks"
import { useLazyGetUnsignedCreativeUrlsQuery } from "@/app/services/download.js"
import { enqueueSnackbar } from "@/features/notifier/notifierSlice.js"
import { selectReadOnlyUser } from "@/features/auth/authSlice.js"

import { downloadS3AsBlob } from "@/util/downloadFromS3.js"
import { getUpdatedAiTags } from "@/util/getUpdatedAiTags.js"
import emptyImage from "@/assets/processing-image.jpeg"

import editIcon from "@/assets/Edit.png"
import iecIcon from "@/assets/icon-iec.png"
import shareIcon from "@/assets/Share.png"
import downloadIcon from "@/assets/Download.png"
import publishIcon from "@/assets/Publish.png"
import applovinIcon from "@/assets/app-lovin-icon.svg"
import unityAddsIcon from "@/assets/unity-adds-icon.svg"
import facebookAddsIcon from "@/assets/fb-adds-icon.svg"
import mintegralIcon from "@/assets/mintegral-icon.svg"
import AdNetworkInfo from "./AdNetworkInfo"
import { getFileName } from "@/util/getFileName"

const drawerWidth = 500

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  justifyContent: "flex-end",
}))

const styles = () => ({
  drawer: {
    overflow: "hidden",
    "& .MuiPaper-root": {
      width: drawerWidth,
    },
  },
  chip: {
    display: "inline-flex",
    padding: "4px 10px",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "10px",
    height: "auto",
    width: "auto",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "normal",
    borderRadius: "4px",
    border: "none",
    background: "#CED5DE",
    textTransform: "capitalize",
    "&.MuiChip-clickable:hover": {
      backgroundColor: "#CED5DE",
      boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
    },
    "& .MuiChip-deleteIcon": {
      width: "12px",
      height: "12px",
      color: "#000",
    },
  },
  viewDetailsBtn: {
    backgroundColor: "#FFCB05",
    color: "#000",
    "&:hover": {
      backgroundColor: "#000",
      color: "#FFCB05",
    },
  },
})

type ExplorerDrawerProps = {
  isOpen: boolean
  data: CreativeObjectType
  handleClose: () => void
  handleClick?: () => void
  url?: string
  unSigndedUrl?: string
}

export default function ExplorerDrawer({
  isOpen,
  handleClose,
  handleClick,
  data,
  url,
}: ExplorerDrawerProps) {
  const customStyles = styles()
  const dispatch = useAppDispatch()
  const ref = React.useRef(null)
  const navigate = useNavigate()

  const isReadOnly = useAppSelector(selectReadOnlyUser)

  const [tooltipTitle, setTooltipTitle] =
    React.useState<string>("Copy Share URL")
  const [isLoadingDownload, setIsLoadingDownload] = React.useState(false)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false)
  const [aiTagData, setAiTagData] = React.useState<AiDataTypes | null>(null)
  const [metaData, setMetadata] =
    React.useState<CreativeAiMetadataTypes | null>(null)
  const [assetCategory, setAssetCategory] = React.useState<{
    display_name: string
    category_id: string
  }>()

  const [deleteCreative, { isLoading: deleteIsLoading }] =
    useDeleteCreativeMutation()
  const { data: assetCategoryData } = useGetAllAssetCategoriesQuery()

  const [getUnsignedCreativeUrls] = useLazyGetUnsignedCreativeUrlsQuery()

  const { data: aniData } = useGetAllNetworksQuery()

  React.useEffect(() => {
    if (isOpen) {
      setAiTagData(getUpdatedAiTags(data))

      setMetadata(
        // @ts-ignore
        !!data.file_metadata ? data.file_metadata.data : null,
      )
    }
  }, [isOpen, data])

  const handleDeleteConfirm = async () => {
    try {
      setIsConfirmModalOpen(false)
      handleClose()
      dispatch(
        enqueueSnackbar({
          message: `Creative deleted successfully`,
          options: {
            key: "creative_delete_success",
            variant: "success",
          },
        }),
      )
    } catch (error) {
      setIsConfirmModalOpen(false)
      handleClose()
      dispatch(
        enqueueSnackbar({
          message: `Creative deletion failed`,
          options: {
            key: "creative_delete_failed",
            variant: "error",
          },
        }),
      )
    }
  }

  const handleDownload = () => {
    if (!!url && !isLoadingDownload) {
      setIsLoadingDownload(true)
      const filename = getFileName({ data, assetCategory })

      downloadS3AsBlob(url, filename).finally(() => {
        setIsLoadingDownload(false)
      })
    }
  }

  const handleTagClick = (key: string, isAiKey = false) => {
    navigate(
      `/file-explorer${
        !localStorage.getItem("isListView") ||
        localStorage.getItem("isListView") === "true"
          ? "/list"
          : "/grid"
      }${
        !localStorage.getItem("tabName") ||
        localStorage.getItem("tabName") === "all"
          ? ""
          : "/" + localStorage.getItem("tabName")
      }?${isAiKey ? "ai_tag=" + key : `tag_${key}=` + data.tag_data[key]}`,
    )
  }

  const handleIECGeneration = (
    name: string,
    type: string,
    tagData: Record<string, string | string[]>,
  ) => {
    if (!!url) {
      const iecData = {
        name,
        type,
        url: url,
        tagData,
      }
      navigate("/iec", { state: iecData })
      handleClose()
    }
  }

  const getAssetCategory = () => {
    if (!!assetCategoryData) {
      setAssetCategory(
        assetCategoryData.find((category) =>
          Object.keys(data.tag_data).some((d) => d === category.category_id),
        ),
      )
    }
  }

  const copyUrlToClipboard = (): void => {
    const textToCopy: string = `${window.location.origin}${window.location.pathname}?asset_id=${data.creative_id}`
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setTooltipTitle("Copied!")
        setTimeout(() => {
          setTooltipTitle("Copy Share URL")
        }, 2000) // Hide tooltip after 2 seconds
      })
      .catch((err: Error) => {
        console.error("Failed to copy: ", err)
      })
  }

  React.useEffect(() => {
    if (!!Object.keys(data).length) {
      getAssetCategory()
    }
  }, [data])

  const filename = getFileName({ data, assetCategory })

  return (
    <Drawer
      sx={customStyles.drawer}
      variant="temporary"
      anchor="right"
      open={isOpen && !!data.file_data}
      ref={ref}
      onClose={handleClose}
      transitionDuration={200}
    >
      {isOpen ? (
        <React.Fragment>
          <DrawerHeader
            sx={{
              height: "72px",
              justifyContent: "space-between",
              px: "20px",
            }}
          >
            <div className="flex">
              <Typography variant="subtitle-3">Detailed View</Typography>
            </div>
            <IconButton sx={{ ml: "auto" }} onClick={handleClose}>
              <Close />
            </IconButton>
          </DrawerHeader>
          {!!data.file_data ? (
            <div className="flex flex-wrap gap-6 p-5">
              <div className="relative w-full">
                {data.file_data.file_type.split("/")[0] === "image" ? (
                  <img
                    src={!!url ? url : emptyImage}
                    alt={data.creative_id}
                    width="100%"
                    style={{
                      objectFit: "cover",
                      borderTopLeftRadius: "30px",
                      height: "350px",
                      border: "1px solid #D9D9D9",
                    }}
                  ></img>
                ) : data.file_data.file_type.split("/")[1] === "html" ? (
                  <HtmlIcon
                    style={{
                      fontSize: "100px",
                      color: "#757575",
                      display: "block",
                      margin: "auto",
                      borderTopLeftRadius: "30px",
                      height: "350px",
                      width: "100%",
                      border: "1px solid #D9D9D9",
                    }}
                  />
                ) : (
                  <video
                    src={!!url ? url : ""}
                    playsInline
                    controls
                    loop
                    width="100%"
                    style={{
                      objectFit: "contain",
                      borderTopLeftRadius: "30px",
                      height: "350px",
                      border: "1px solid #D9D9D9",
                    }}
                    controlsList="nodownload"
                    disablePictureInPicture
                  ></video>
                )}
              </div>
              <div className="flex flex-col gap-8 px-[18px] w-full">
                <div className="flex justify-between">
                  <div className="flex gap-[6px] items-center max-w-[140px]">
                    {!isReadOnly ? (
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <Button
                            iconButton
                            variant="text"
                            component={TooltipTrigger}
                            className="min-w-4 min-h-4 p-0"
                            onClick={handleClick}
                          >
                            <img src={editIcon} className="size-4" />
                          </Button>
                          <TooltipContent
                            side="bottom"
                            className="z-[10000000]"
                          >
                            <Typography variant="body-medium-small">
                              Edit Tags
                            </Typography>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : null}
                    {!isReadOnly ? (
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <Button
                            iconButton
                            variant="text"
                            component={TooltipTrigger}
                            disabled={
                              data.file_data.file_type.split("/")[1] === "html"
                            }
                            className="min-w-4 min-h-4 p-0"
                            onClick={() =>
                              handleIECGeneration(
                                filename,
                                data.file_data.file_type,
                                data.tag_data,
                              )
                            }
                          >
                            <img src={iecIcon} className="size-4" />
                          </Button>
                          <TooltipContent
                            side="bottom"
                            className="z-[10000000]"
                          >
                            <Typography variant="body-medium-small">
                              Convert To IEC
                            </Typography>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : null}
                    {!isReadOnly ? (
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <Button
                            iconButton
                            variant="text"
                            component={TooltipTrigger}
                            className="min-w-4 min-h-4 p-0"
                            onClick={copyUrlToClipboard}
                          >
                            <img src={shareIcon} className="size-4" />
                          </Button>
                          <TooltipContent
                            side="bottom"
                            className="z-[10000000]"
                          >
                            <Typography variant="body-medium-small">
                              Share Asset URL
                            </Typography>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : null}
                    {isLoadingDownload ? (
                      <CircularProgress
                        size="16px"
                        sx={{ display: "inherit" }}
                      />
                    ) : (
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <Button
                            iconButton
                            variant="text"
                            component={TooltipTrigger}
                            className="min-w-4 min-h-4 p-0"
                            onClick={handleDownload}
                          >
                            <img src={downloadIcon} className="size-4" />
                          </Button>
                          <TooltipContent
                            side="bottom"
                            className="z-[10000000]"
                          >
                            <Typography variant="body-medium-small">
                              Download Asset
                            </Typography>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    {!isReadOnly ? (
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <Button
                            iconButton
                            variant="text"
                            component={TooltipTrigger}
                            className="min-w-4 min-h-4 p-0"
                            onClick={() => {
                              navigate("/add-network", {
                                state: {
                                  fileName: filename,
                                  fileType: data.file_data.file_type,
                                  fileSize:
                                    data.file_metadata.data.file_size || "",
                                  length:
                                    data.file_metadata.data.duration || "",
                                  dimensions:
                                    data.file_metadata.data.dimensions || "",
                                  id: data.creative_id,
                                  fileUrl: url,
                                },
                              })
                            }}
                          >
                            <img src={publishIcon} className="size-4" />
                          </Button>
                          <TooltipContent
                            side="bottom"
                            className="z-[10000000]"
                          >
                            <Typography variant="body-medium-small">
                              Publish to Ad Networks
                            </Typography>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : null}
                    {/* {!isReadOnly ? (
                                        <Button
                                          variant="text"
                                          
                                          onClick={() => {
                                            onDelete(row)
                                          }}
                                        >
                                          <DeleteOutlined
                                            sx={{ fill: "#EB4335", fontSize: "20px" }}
                                          />
                                          Delete
                                        </Button>
                                      ) : null} */}
                  </div>
                  {/* {!isReadOnly ? (
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <Button
                          iconButton
                          variant="text"
                          component={TooltipTrigger}
                          className="min-w-7 min-h-4 p-0"
                          onClick={copyUnsignedUrlToClipboard}
                        >
                          <img src={crossPromoIcon} className="size-4" />
                        </Button>
                        <TooltipContent side="bottom">
                          <Typography variant="body-medium-small">
                            Get Cross Promo URL
                          </Typography>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : null} */}
                </div>
                <div className="flex justify-between">
                  <div className="flex gap-4">
                    <div className="flex flex-col gap-4">
                      {!!data.file_data.filename ? (
                        <Typography
                          variant="body-medium-small"
                          className="text-neutral-900"
                        >
                          File Name :
                        </Typography>
                      ) : null}
                      {!!Object.keys(data.file_metadata.data).length ? (
                        <React.Fragment>
                          <Typography
                            variant="body-medium-small"
                            className="text-neutral-900"
                          >
                            Size :
                          </Typography>
                          <Typography
                            variant="body-medium-small"
                            className="text-neutral-900"
                          >
                            Dimensions :
                          </Typography>
                          {!!data.file_metadata.data.duration ? (
                            <Typography
                              variant="body-medium-small"
                              className="text-neutral-900"
                            >
                              Length :
                            </Typography>
                          ) : null}
                        </React.Fragment>
                      ) : null}
                      {data.file_data.file_type ? (
                        <Typography
                          variant="body-medium-small"
                          className="text-neutral-900"
                        >
                          File Type :
                        </Typography>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-4">
                      {!!data.file_data.filename ? (
                        <MuiTooltip title={filename}>
                          <Typography
                            variant="body-medium-small"
                            className="text-neutral-400 !truncate max-w-[120px]"
                          >
                            {filename}
                          </Typography>
                        </MuiTooltip>
                      ) : null}
                      {!!Object.keys(data.file_metadata.data).length ? (
                        <React.Fragment>
                          <Typography
                            variant="body-medium-small"
                            className="text-neutral-400"
                          >
                            {`${(
                              data.file_metadata.data.file_size /
                              (1024 * 1024)
                            ).toFixed(2)} MB`}
                          </Typography>
                          <Typography
                            variant="body-medium-small"
                            className="text-neutral-400"
                          >
                            {data.file_metadata.data.dimensions.join(" ")}
                          </Typography>
                          {!!data.file_metadata.data.duration ? (
                            <Typography
                              variant="body-medium-small"
                              className="text-neutral-400"
                            >
                              {data.file_metadata.data.duration}
                            </Typography>
                          ) : null}
                        </React.Fragment>
                      ) : null}
                      {data.file_data.file_type ? (
                        <Typography
                          variant="body-medium-small"
                          className="text-neutral-400"
                        >
                          {data.file_data.file_type}
                        </Typography>
                      ) : null}
                    </div>
                  </div>
                  {!!assetCategory ? (
                    <div className="flex justify-center items-center h-[30px] rounded-[20px] bg-neutral-150 p-4">
                      <Typography variant="caption">
                        {assetCategory.display_name}
                      </Typography>
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-6">
                  <Typography variant="subtitle-3">
                    Publishing Status
                  </Typography>
                  <div className="flex items-center gap-10">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-1">
                        <img src={applovinIcon} />
                        <Typography
                          variant="body-medium-small"
                          className="text-neutral-900 font-normal"
                        >
                          AppLovin
                        </Typography>
                      </div>
                      <div className="flex items-center gap-1">
                        <img src={unityAddsIcon} />
                        <Typography
                          variant="body-medium-small"
                          className="text-neutral-900 font-normal"
                        >
                          Unity Ads
                        </Typography>
                      </div>
                      <div className="flex items-center gap-1">
                        <img src={facebookAddsIcon} />
                        <Typography
                          variant="body-medium-small"
                          className="text-neutral-900 font-normal"
                        >
                          Facebook
                        </Typography>
                      </div>
                      <div className="flex items-center gap-1">
                        <img src={mintegralIcon} />
                        <Typography
                          variant="body-medium-small"
                          className="text-neutral-900 font-normal"
                        >
                          Mintegral
                        </Typography>
                      </div>
                    </div>
                    {!!data.ad_network_data &&
                    !!Object.keys(data.ad_network_data).length ? (
                      <div className="flex flex-col gap-4 items-start">
                        <AdNetworkInfo
                          last_published={
                            data.ad_network_data["applovin"]?.last_published
                          }
                          data={data.ad_network_data["applovin"]?.data}
                        />
                        <AdNetworkInfo
                          last_published={
                            data.ad_network_data["unity"]?.last_published
                          }
                          data={data.ad_network_data["unity"]?.data}
                        />
                        <AdNetworkInfo
                          last_published={
                            data.ad_network_data["facebook"]?.last_published
                          }
                          data={data.ad_network_data["facebook"]?.data}
                        />
                        <AdNetworkInfo
                          last_published={
                            data.ad_network_data["mintegral"]?.last_published
                          }
                          data={data.ad_network_data["mintegral"]?.data}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          <div className="flex px-10 py-6 bg-space-blue-150">
            <div className="flex flex-col gap-6">
              {!!Object.keys(data.tag_data).length ? (
                <div className="flex flex-col items-start gap-3">
                  <Typography
                    variant="body-medium-small"
                    className="!font-semibold"
                  >
                    Tags
                  </Typography>
                  <div className="flex gap-[18px] flex-wrap">
                    {Object.keys(data.tag_data).map((key) => {
                      if (
                        !!assetCategory &&
                        key === assetCategory.category_id
                      ) {
                        return null
                      } else {
                        return (
                          <Chip
                            key={key}
                            label={<span>{data.tag_data[key]}</span>}
                            variant="outlined"
                            onClick={() => handleTagClick(key)}
                            sx={customStyles.chip}
                          />
                        )
                      }
                    })}
                  </div>
                </div>
              ) : null}
              {!!aiTagData && !!aiTagData.ai_tag_data.labels_names.length ? (
                <div className="flex flex-col items-start gap-3">
                  <Typography
                    variant="body-medium-small"
                    className="!font-semibold"
                  >
                    AI Tags
                  </Typography>
                  <div className="flex gap-[18px] flex-wrap">
                    {aiTagData.ai_tag_data.labels_names.map((key) => (
                      <Chip
                        key={key}
                        label={<span>{key}</span>}
                        variant="outlined"
                        onClick={() => handleTagClick(key, true)}
                        sx={customStyles.chip}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          {/* <div className="flex pb-2">
            <div className="flex flex-col gap-[30px]">
              {!!Object.keys(data.tag_data).length ? (
                <div className="flex flex-col items-start gap-3">
                  <Typography fontWeight={700} variant="subtitle2">
                    Tags
                  </Typography>
                  <div className="flex gap-[18px] flex-wrap">
                    {Object.keys(data.tag_data).map((key) => {
                      if (
                        !!assetCategory &&
                        key === assetCategory.category_id
                      ) {
                        return null
                      } else {
                        return (
                          <Chip
                            key={key}
                            label={data.tag_data[key]}
                            variant="outlined"
                            onClick={() => handleTagClick(key)}
                            sx={customStyles.chip}
                          />
                        )
                      }
                    })}
                  </div>
                </div>
              ) : null}
              {!!aiTagData &&
              !!aiTagData.ai_tag_data.labels_names.length ? (
                <div className="flex flex-col items-start gap-3">
                  <Typography fontWeight={700} variant="subtitle2">
                    Ai Tags
                  </Typography>
                  <div className="flex gap-[18px]">
                    {aiTagData.ai_tag_data.labels_names.map((key) => (
                      <Chip
                        key={key}
                        label={key}
                        variant="outlined"
                        onClick={() => handleTagClick(key, true)}
                        sx={customStyles.chip}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div> */}
        </React.Fragment>
      ) : null}
      <ConfirmModal
        open={isConfirmModalOpen}
        confirmText="Do you really want to delete the asset, this action cannot be reversed"
        handleClose={() => {
          setIsConfirmModalOpen(false)
          handleClose()
        }}
        isLoading={deleteIsLoading}
        handleSubmit={() => handleDeleteConfirm()}
      />
    </Drawer>
  )
}
